import { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { getUserRecipientLists, deleteRecipientList } from "../../firebase/recipientLists";
import RecipientListModal from "./RecipientListModal";
import Modal from "../common/Modal";

export default function RecipientListsPanel() {
  const { user } = useAuth();

  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [editing, setEditing] = useState(null); // "new" | list | null
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const load = async () => {
    if (!user) return;
    setLoading(true);
    setLoadError(null);
    try {
      setLists(await getUserRecipientLists(user.uid));
    } catch (err) {
      console.error("Failed to load recipient lists:", err);
      setLoadError("We couldn't load your recipient lists. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    try {
      await deleteRecipientList(deleteTarget.id);
      setLists((prev) => prev.filter((l) => l.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (err) {
      console.error("Failed to delete recipient list:", err);
    } finally {
      setDeleteBusy(false);
    }
  };

  return (
    <div>
      <div className="mb-6 flex items-center justify-between gap-3">
        <p className="text-sm text-gray-500">
          Reusable recipient lists you can attach to any scheduled report.
        </p>
        <button
          onClick={() => setEditing("new")}
          className="bg-blue-600 hover:bg-blue-700 transition px-4 py-2.5 rounded-xl font-semibold text-sm shrink-0"
        >
          + New List
        </button>
      </div>

      {loading && (
        <div className="py-16 flex flex-col items-center text-gray-500">
          <span className="w-8 h-8 border-4 border-gray-800 border-t-blue-500 rounded-full animate-spin mb-3" />
          Loading recipient lists...
        </div>
      )}

      {loadError && !loading && (
        <div className="bg-red-950/30 border border-red-800/50 rounded-xl px-4 py-3 text-sm text-red-300">
          {loadError}
        </div>
      )}

      {!loading && !loadError && lists.length === 0 && (
        <div className="text-center py-16 border border-dashed border-gray-800 rounded-2xl">
          <div className="text-3xl mb-3" aria-hidden="true">👥</div>
          <p className="text-gray-300 font-medium">No recipient lists yet</p>
          <p className="text-gray-500 text-sm mt-1">
            Create one, like &ldquo;Executives&rdquo; or &ldquo;Sales Team&rdquo;, to reuse across reports.
          </p>
        </div>
      )}

      <div className="space-y-3">
        {lists.map((list) => (
          <div key={list.id} className="bg-gray-900 border border-gray-800 rounded-2xl p-4 sm:p-5 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <h3 className="font-semibold text-white truncate">{list.name}</h3>
              <p className="text-xs text-gray-500 mt-1">
                {list.recipients?.length || 0} recipient{(list.recipients?.length || 0) === 1 ? "" : "s"}
              </p>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => setEditing(list)}
                className="text-xs px-3 py-1.5 rounded-lg bg-gray-800 hover:bg-gray-700 border border-gray-700 transition font-medium"
              >
                Edit
              </button>
              <button
                onClick={() => setDeleteTarget(list)}
                className="text-xs px-3 py-1.5 rounded-lg bg-red-950/40 hover:bg-red-950/60 border border-red-900/50 text-red-300 transition font-medium"
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      <RecipientListModal
        open={!!editing}
        onClose={() => setEditing(null)}
        uid={user?.uid}
        existing={editing && editing !== "new" ? editing : null}
        onSaved={load}
      />

      <Modal
        open={!!deleteTarget}
        onClose={() => !deleteBusy && setDeleteTarget(null)}
        dismissible={!deleteBusy}
        title="Delete this list?"
        footer={
          <>
            <button
              onClick={() => setDeleteTarget(null)}
              disabled={deleteBusy}
              className="px-4 py-2.5 rounded-xl bg-gray-800 hover:bg-gray-700 border border-gray-700 transition text-sm font-medium disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={confirmDelete}
              disabled={deleteBusy}
              className="px-4 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 transition text-sm font-semibold disabled:opacity-50"
            >
              {deleteBusy ? "Deleting..." : "Delete Permanently"}
            </button>
          </>
        }
      >
        <p>
          <span className="text-white font-medium">{deleteTarget?.name}</span> will be removed. Any schedule
          using it will simply stop including these recipients — nothing else is affected. This can't be undone.
        </p>
      </Modal>
    </div>
  );
}
